import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

const LOCAL_STORAGE_KEY = 'SIDEBAR_STATE';
const DEFAULT_EXPANDED = false;

type SidebarStateContextValue = {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
};

const defaultContextValue: SidebarStateContextValue = {
  expanded: DEFAULT_EXPANDED,
  setExpanded: () => {},
};

const SidebarStateContext = createContext<SidebarStateContextValue>(defaultContextValue);

interface SidebarStateProviderProps {
  children?: ReactNode;
}

/**
 * Validates if a value is a valid boolean
 * @param value - The value to validate
 * @returns true if the value is a boolean
 */
const isValidBoolean = (value: unknown): value is boolean => {
  return typeof value === 'boolean';
};

/**
 * Safely parses and validates sidebar state from localStorage
 * @returns The expanded state, or default if invalid/not found
 */
const restoreSidebarState = (): boolean => {
  try {
    const storedData = window.localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!storedData) {
      return DEFAULT_EXPANDED;
    }

    const parsed: unknown = JSON.parse(storedData);

    // Validate that parsed data is a boolean
    if (!isValidBoolean(parsed)) {
      console.warn('SidebarStateProvider: Invalid data format in localStorage, expected boolean');
      return DEFAULT_EXPANDED;
    }

    return parsed;
  } catch (err) {
    // JSON.parse failed or other error
    console.warn('SidebarStateProvider: Failed to parse localStorage data', err);
    return DEFAULT_EXPANDED;
  }
};

/**
 * Safely stores sidebar state to localStorage
 * @param expanded - The expanded state to store
 */
const storeSidebarState = (expanded: boolean): void => {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(expanded));
  } catch (err) {
    // localStorage might be full or disabled
    console.warn('SidebarStateProvider: Failed to save to localStorage', err);
  }
};

/**
 * Provider component for managing sidebar expanded state with localStorage persistence.
 *
 * @example
 * ```tsx
 * <SidebarStateProvider>
 *   <App />
 * </SidebarStateProvider>
 * ```
 */
export const SidebarStateProvider: FC<SidebarStateProviderProps> = ({ children }) => {
  const [expanded, setExpandedState] = useState<boolean>(DEFAULT_EXPANDED);

  // Initialize from localStorage on mount
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    const restored = restoreSidebarState();
    setExpandedState(restored);
  }, []);

  const setExpanded = useCallback((newExpanded: boolean) => {
    // Validate the value before setting
    if (!isValidBoolean(newExpanded)) {
      console.warn(`SidebarStateProvider: Attempted to set invalid expanded value "${newExpanded}"`);
      return;
    }

    setExpandedState(newExpanded);
    storeSidebarState(newExpanded);
  }, []);

  const value = useMemo<SidebarStateContextValue>(
    () => ({
      expanded,
      setExpanded,
    }),
    [expanded, setExpanded]
  );

  return <SidebarStateContext.Provider value={value}>{children}</SidebarStateContext.Provider>;
};

/**
 * Hook to access and manage sidebar expanded state.
 *
 * @returns An object containing:
 * - `expanded`: Boolean indicating if the sidebar is expanded
 * - `setExpanded`: Function to set the expanded state
 *
 * @example
 * ```tsx
 * const { expanded, setExpanded } = useSidebarState();
 *
 * // Set sidebar to expanded
 * setExpanded(true);
 *
 * // Set sidebar to collapsed
 * setExpanded(false);
 * ```
 */
export const useSidebarState = (): SidebarStateContextValue => {
  const context = useContext(SidebarStateContext);

  if (context === undefined) {
    // Return safe default if used outside provider (shouldn't happen but be resilient)
    console.warn('useSidebarState must be used within a SidebarStateProvider');
    return defaultContextValue;
  }

  return context;
};
